import type { Client } from "@/.generated/hey/v4b-backend/client";
import type { ProgramId, ProgrammingRangeId, SiteSectionCategoryId, SiteSectionId, V4HSiteId } from "@/data/common/ids";
import type {
  EProgramApplianceCategory,
  ProgramDetails,
  SiteProgramOverview,
} from "@/domains/v4b/program/program.types";
import type {
  CreateSiteSectionProgrammingRangeRequest,
  UpdateSiteSectionProgrammingRangeRequest,
} from "@/domains/v4b/site-programming-range/site-programming-range.types";
import type { SectionNodePlanning } from "@/domains/v4b/site-programming-range/site-programming-range.types";
import type {
  BulkMoveAppliancesRequest,
  CreatedSiteSection,
  CreatedSiteSectionCategory,
  CreateSectionCategoryRequest,
  CreateSiteSectionRequest,
  MoveSectionRequest,
  PatchSiteSectionRequest,
  ProgrammableAppliance,
  SectionCategory,
  SectionDeletability,
  SectionForest,
  UpdateSectionCategoryRequest,
  UpdateSectionsCategoriesRequest,
} from "@/domains/v4b/site-section/site-section.types";
import type { z } from "zod";

import {
  bulkMoveAppliances,
  createProgram,
  createSectionCategory,
  createSectionProgrammingRanges,
  createSiteSection,
  deleteProgrammingRange,
  deleteSection,
  deleteSectionCategory,
  findAllV2,
  findSectionForest,
  getSectionProgrammingsPlanning,
  getSiteConsumptions,
  getSiteProgram,
  getSitePrograms,
  getSiteSectionCategories,
  isSectionDeletable,
  moveSection,
  patchSiteSection,
  updateProgram,
  updateProgrammingRange,
  updateSectionCategory,
  updateSectionsCategories,
  updateSubprograms,
} from "@/.generated/hey/v4b-backend/sdk.gen";
import {
  zCreateProgramResponse,
  zCreateSectionCategoryResponse,
  zCreateSiteSectionResponse,
  zFindAllV2Response,
  zFindSectionForestResponse,
  zGetSectionProgrammingsPlanningResponse,
  zGetSiteConsumptionsResponse,
  zGetSiteProgramResponse,
  zGetSiteProgramsResponse,
  zGetSiteSectionCategoriesResponse,
  zIsSectionDeletableResponse,
} from "@/.generated/hey/v4b-backend/zod.gen";
import { useSentry } from "@/composables/sentry/use-sentry";
import { V4bClientError, V4bClientUnknownError } from "@/data/api/v4b/error/v4b-client-error.types";
import { mapV4bApiError } from "@/data/api/v4b/error/v4b-error-mapping";
import { mapCreateProgramResponse } from "@/data/api/v4b/program/program-creation.mapping";
import { mapProgramDetails, mapSiteProgramOverviews } from "@/data/api/v4b/program/program.mapping";
import { mapMonositeConsumption } from "@/data/api/v4b/site-consumption/site-consumption.mapping";
import { mapSectionNodePlanning } from "@/data/api/v4b/site-programming-range/site-programming-range.mapping";
import {
  mapCreatedSiteSectionCategory,
  mapCreatedSiteSection,
  mapProgrammableAppliances,
  mapSectionCategories,
  mapSectionDeletability,
  mapSectionForest,
} from "@/data/api/v4b/site-section/site-section.mapping";
import { CreateProgramRequest, CreateProgramResponse } from "@/domains/v4b/program/program-creation.types";
import { UpdateProgramRequest } from "@/domains/v4b/program/program-update.types";
import { SubprogramsByApplianceCategoryRequest } from "@/domains/v4b/program/subprogram.types";
import { MonositeConsumption } from "@/domains/v4b/site-consumption/monosite-consumption.types";
import { ConsumptionRequest } from "@/domains/v4b/site-consumption/site-consumption.types";

export type V4bClientSdk = ReturnType<typeof V4bClientSdk>;

export function V4bClientSdk(client: Client) {
  const requestOpts = {
    client,
    headers: {
      "Content-Type": "application/json",
    },
    throwOnError: false,
  } as const;

  return {
    appliance: {
      bulkMove(siteId: V4HSiteId, body: BulkMoveAppliancesRequest): Promise<void> {
        return bulkMoveAppliances({ ...requestOpts, path: { siteId }, body }).then(handleEmptyResult);
      },
    },
    siteConsumption: {
      findSiteConsumption(siteId: V4HSiteId, body: ConsumptionRequest): Promise<MonositeConsumption> {
        return getSiteConsumptions({ ...requestOpts, path: { siteId }, body })
          .then(bestEffortValidate(zGetSiteConsumptionsResponse))
          .then(mapMonositeConsumption);
      },
    },
    siteProgram: {
      findAll(siteId: V4HSiteId): Promise<SiteProgramOverview[]> {
        return getSitePrograms({ ...requestOpts, path: { siteId } })
          .then(bestEffortValidate(zGetSiteProgramsResponse))
          .then(mapSiteProgramOverviews);
      },
      getProgramDetails(
        programId: ProgramId,
        applianceCategories: EProgramApplianceCategory[],
      ): Promise<ProgramDetails> {
        return getSiteProgram({ ...requestOpts, path: { programId }, query: { applianceCategories } })
          .then(bestEffortValidate(zGetSiteProgramResponse))
          .then(mapProgramDetails);
      },
      createProgram(body: CreateProgramRequest): Promise<CreateProgramResponse> {
        return createProgram({ ...requestOpts, body })
          .then(bestEffortValidate(zCreateProgramResponse))
          .then(mapCreateProgramResponse);
      },
      updateProgram(programId: ProgramId, body: UpdateProgramRequest): Promise<void> {
        return updateProgram({ ...requestOpts, path: { programId }, body }).then(handleEmptyResult);
      },
      updateSubprograms(programId: ProgramId, body: SubprogramsByApplianceCategoryRequest): Promise<void> {
        return updateSubprograms({ ...requestOpts, path: { programId }, body }).then(handleEmptyResult);
      },
    },
    siteProgrammingRange: {
      create(siteId: V4HSiteId, body: CreateSiteSectionProgrammingRangeRequest): Promise<void> {
        return createSectionProgrammingRanges({ ...requestOpts, path: { siteId }, body }).then(handleEmptyResult);
      },
      update(
        siteId: V4HSiteId,
        sectionId: SiteSectionId,
        body: UpdateSiteSectionProgrammingRangeRequest,
      ): Promise<void> {
        return updateProgrammingRange({ ...requestOpts, path: { siteId, sectionId }, body }).then(handleEmptyResult);
      },
      delete(siteId: V4HSiteId, sectionId: SiteSectionId, programmingRangeUuid: ProgrammingRangeId): Promise<void> {
        return deleteProgrammingRange({
          ...requestOpts,
          path: { siteId, sectionId },
          body: { programmingRangeUuid },
        }).then(handleEmptyResult);
      },
      findPlanning(siteId: V4HSiteId): Promise<SectionNodePlanning[]> {
        return getSectionProgrammingsPlanning({ ...requestOpts, path: { siteId } })
          .then(bestEffortValidate(zGetSectionProgrammingsPlanningResponse))
          .then(mapSectionNodePlanning);
      },
    },
    siteSection: {
      create(siteId: V4HSiteId, body: CreateSiteSectionRequest): Promise<CreatedSiteSection> {
        return createSiteSection({ ...requestOpts, path: { siteId }, body })
          .then(bestEffortValidate(zCreateSiteSectionResponse))
          .then(mapCreatedSiteSection);
      },
      findForest(siteId: V4HSiteId): Promise<SectionForest> {
        return findSectionForest({ ...requestOpts, path: { siteId } })
          .then(bestEffortValidate(zFindSectionForestResponse))
          .then(mapSectionForest);
      },
      delete(siteId: V4HSiteId, sectionId: SiteSectionId): Promise<void> {
        return deleteSection({ ...requestOpts, path: { siteId, sectionId } }).then(handleEmptyResult);
      },
      patch(sectionId: SiteSectionId, body: PatchSiteSectionRequest): Promise<void> {
        return patchSiteSection({ ...requestOpts, path: { sectionId }, body }).then(handleEmptyResult);
      },
      isDeletable(sectionId: SiteSectionId): Promise<SectionDeletability> {
        return isSectionDeletable({ ...requestOpts, path: { sectionId } })
          .then(bestEffortValidate(zIsSectionDeletableResponse))
          .then(mapSectionDeletability);
      },
      move(sectionId: SiteSectionId, body: MoveSectionRequest): Promise<void> {
        return moveSection({ ...requestOpts, path: { sectionId }, body }).then(handleEmptyResult);
      },
      findAppliances(sectionId: SiteSectionId): Promise<ProgrammableAppliance[]> {
        return findAllV2({ ...requestOpts, path: { sectionId } })
          .then(bestEffortValidate(zFindAllV2Response))
          .then(mapProgrammableAppliances);
      },
    },
    siteSectionCategory: {
      findAll(siteId: V4HSiteId): Promise<SectionCategory[]> {
        return getSiteSectionCategories({ ...requestOpts, path: { siteId } })
          .then(bestEffortValidate(zGetSiteSectionCategoriesResponse))
          .then(mapSectionCategories);
      },
      create(siteId: V4HSiteId, body: CreateSectionCategoryRequest): Promise<CreatedSiteSectionCategory> {
        return createSectionCategory({ ...requestOpts, path: { siteId }, body })
          .then(bestEffortValidate(zCreateSectionCategoryResponse))
          .then(mapCreatedSiteSectionCategory);
      },
      update(
        siteId: V4HSiteId,
        sectionCategoryId: SiteSectionCategoryId,
        body: UpdateSectionCategoryRequest,
      ): Promise<void> {
        return updateSectionCategory({ ...requestOpts, path: { siteId, sectionCategoryId }, body }).then(
          handleEmptyResult,
        );
      },
      delete(siteId: V4HSiteId, sectionCategoryId: SiteSectionCategoryId): Promise<void> {
        return deleteSectionCategory({ ...requestOpts, path: { siteId, sectionCategoryId } }).then(handleEmptyResult);
      },
      updateSectionsCategories(siteId: V4HSiteId, body: UpdateSectionsCategoriesRequest): Promise<void> {
        return updateSectionsCategories({ ...requestOpts, path: { siteId }, body }).then(handleEmptyResult);
      },
    },
  };
}

// V4b operations type their error payload as `unknown` (the 401/403 responses widen the union),
// so we narrow back to the common `JsonErrorWrapper` shape at runtime.
interface ApiResult<T> {
  request: Request;
  response: Response;
  data: T | undefined;
  error?: unknown;
}

/**
 * Fail fast in case of response error. This should always be used to handle responses from v4b-backend.
 */
function handleResult<T>(result: ApiResult<T>): {
  request: Request;
  response: Response;
  data: T;
} {
  const { request, response, data } = handleNonSuccessfulResult(result);
  if (!data) {
    throw new V4bClientUnknownError(request, response);
  }

  return { request, response, data };
}

function handleEmptyResult(result: ApiResult<unknown>): void {
  handleNonSuccessfulResult(result);
}

function handleNonSuccessfulResult<T>(result: ApiResult<T>): ApiResult<T> {
  const { request, response, error } = result;
  if (response.ok) {
    return result;
  }

  const apiError = mapV4bApiError(error);

  if (apiError) {
    throw new V4bClientError(request, response, apiError);
  }

  throw new V4bClientUnknownError(request, response);
}

/**
 * Validate a response at runtime, but does not block the flow in case of validation failure.
 */
function bestEffortValidate<T>(validator: z.ZodTypeAny) {
  return (result: ApiResult<T>): T => {
    const { data } = handleResult(result);

    const parseResult = validator.safeParse(data);
    if (parseResult.success) {
      // Return the original (generated-typed) data rather than the zod-inferred shape,
      // which can be looser for recursive schemas.
      return data;
    }

    const errorData = {
      validationError: parseResult.error,
      receivedData: data,
    };
    console.error("Backend validation error : ", { errorData });

    useSentry().captureException(new Error("Backend response does not match OpenAPI contract"));

    // Unsafe cast : see exception captured above
    return data as T;
  };
}
